import {
  useApiUrl,
  useCreate,
  useCustomMutation,
  useInvalidate,
  useList,
  useUpdate,
} from '@refinedev/core';
import { notification } from 'antd';
import { ROUTES } from 'common/constants';
import { useWebSocket } from 'common/hooks/use-websocket/use-websocket.hook';
import { useNavigateWithOrgAndFarm } from 'common/utils';
import React, { useCallback, useState } from 'react';
import { useOrganization } from 'src/pages/organization';
import { BlueColors } from 'theme';

import { NotificationToast } from './notification-toast/notification-toast.component';
import {
  NotificationTabEnum,
  NotificationTypeEnum,
} from './notifications.enum';
import {
  NotificationObjectData,
  NotificationWebItem,
} from './notifications.model';
import {
  getNotificationContent,
  isBudgetNotificationType,
  parseObjectData,
} from './notifications.util';

const PAGE_SIZE = 100;
const WS_NOTIFICATION_URL = import.meta.env.VITE_WS_NOTIFICATION_URL ?? '';

const useNotifications = (onClose?: () => void) => {
  const apiUrl = useApiUrl();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigateWithOrgAndFarm();

  const [activeTab, setActiveTab] = useState<NotificationTabEnum>(
    NotificationTabEnum.ALL,
  );
  const [page, setPage] = useState(1);

  const isUnreadFilter = activeTab === NotificationTabEnum.UNREAD;

  const {
    data: notificationsData,
    isLoading,
    isFetching,
  } = useList<NotificationWebItem>({
    resource: 'notification-web',
    meta: {
      organizationId: currentOrganizationId,
    },
    queryOptions: {
      enabled: !!currentOrganizationId,
    },
    pagination: {
      current: page,
      pageSize: PAGE_SIZE,
    },
    filters: isUnreadFilter
      ? [{ field: 'read', operator: 'eq', value: false }]
      : [],
  });

  const { data: unreadCountData } = useList<NotificationWebItem>({
    resource: 'notification-web',
    meta: {
      organizationId: currentOrganizationId,
    },
    queryOptions: {
      enabled: !!currentOrganizationId,
    },
    pagination: {
      current: 1,
      pageSize: 1,
    },
    filters: [{ field: 'read', operator: 'eq', value: false }],
  });

  const { mutate } = useUpdate();
  const { mutate: mutateMarkAllAsRead, status: statusAllAsRead } =
    useCustomMutation();
  const invalidate = useInvalidate();

  const notifications = notificationsData?.data ?? [];
  const totalElements = notificationsData?.total ?? 0;
  const totalPages = Math.ceil(totalElements / PAGE_SIZE) || 1;
  const hasMore = page < totalPages;
  const unreadCount = unreadCountData?.total ?? 0;

  const handleTabChange = useCallback((tab: NotificationTabEnum) => {
    setActiveTab(tab);
    setPage(1);
  }, []);

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const invalidateNotifications = useCallback(() => {
    invalidate({
      resource: 'notification-web',
      invalidates: ['list'],
    });
  }, [invalidate]);

  const handleMarkAllAsRead = useCallback(() => {
    mutateMarkAllAsRead(
      {
        url: `${apiUrl}/notification-web/batch/mark-all-read?organizationId=${currentOrganizationId}`,
        method: 'put',
        values: {},
      },
      {
        onSuccess: () => {
          invalidateNotifications();
        },
      },
    );
  }, [
    apiUrl,
    currentOrganizationId,
    mutateMarkAllAsRead,
    invalidateNotifications,
  ]);

  const handleNotificationClick = useCallback(
    (item: NotificationWebItem) => {
      if (!item.read) {
        mutate(
          {
            resource: 'notification-web',
            id: item.id,
            values: { read: true },
            meta: {
              organizationId: currentOrganizationId,
            },
            successNotification: false,
          },
          {
            onSuccess: () => {
              invalidateNotifications();
            },
          },
        );
      }

      if (item.type === NotificationTypeEnum.ITEM_STOCK_LOW) {
        const data = parseObjectData(item.objectData);
        if (data) {
          navigate(
            `${ROUTES.STOCK}`,
            undefined,
            undefined,
            undefined,
            undefined,
            [
              { field: 'stockLow', operator: 'eq', value: true },
              { field: 'itemCode', operator: 'eq', value: data.itemCode },
            ],
          );
          onClose?.();
        }
      }

      if (isBudgetNotificationType(item.type)) {
        const data = parseObjectData(item.objectData);
        if (data) {
          navigate(
            ROUTES.FINANCIAL_BUDGET,
            undefined,
            undefined,
            undefined,
            undefined,
            [
              { field: 'farm', operator: 'eq', value: data.farm },
              {
                field: 'seasonId',
                operator: 'eq',
                value: data.seasonExternalId,
              },
            ],
          );
          onClose?.();
        }
      }
    },
    [
      apiUrl,
      currentOrganizationId,
      mutate,
      navigate,
      onClose,
      invalidateNotifications,
    ],
  );

  return {
    notifications,
    isLoading,
    isFetching,
    activeTab,
    handleTabChange,
    handleLoadMore,
    handleMarkAllAsRead,
    isMarkingAllAsRead: statusAllAsRead === 'loading',
    handleNotificationClick,
    hasMore,
    totalElements,
    unreadCount,
  };
};

const useNotificationUnreadCount = () => {
  const apiUrl = useApiUrl();
  const { currentOrganizationId } = useOrganization();
  const invalidate = useInvalidate();
  const navigate = useNavigateWithOrgAndFarm();
  const { mutate: reprocessNotifications } = useCreate();

  const { data: unreadCountData } = useList<NotificationWebItem>({
    resource: 'notification-web',
    meta: {
      organizationId: currentOrganizationId,
    },
    queryOptions: {
      enabled: !!currentOrganizationId,
    },
    pagination: {
      current: 1,
      pageSize: 1,
    },
    filters: [{ field: 'read', operator: 'eq', value: false }],
  });

  const navigateToStock = useCallback(
    (objectData: NotificationObjectData) => {
      navigate(ROUTES.STOCK, undefined, undefined, undefined, undefined, [
        { field: 'stockLow', operator: 'eq', value: true },
        { field: 'itemCode', operator: 'eq', value: objectData.itemCode },
      ]);
    },
    [navigate],
  );

  const navigateToBudget = useCallback(
    (objectData: NotificationObjectData) => {
      navigate(
        ROUTES.FINANCIAL_BUDGET,
        undefined,
        undefined,
        undefined,
        undefined,
        [
          { field: 'farm', operator: 'eq', value: objectData.farm },
          {
            field: 'seasonId',
            operator: 'eq',
            value: objectData.seasonExternalId,
          },
        ],
      );
    },
    [navigate],
  );

  const showToast = useCallback(
    (data: {
      message: string;
      objectData?: NotificationObjectData;
      type?: string;
    }) => {
      const key = Date.now().toString();
      const objectData = data.objectData;

      const handleDismiss = () => {
        notification.destroy(key);
      };

      const getViewHandler = () => {
        if (!objectData) return undefined;

        if (data.type === NotificationTypeEnum.ITEM_STOCK_LOW) {
          return () => {
            notification.destroy(key);
            navigateToStock(objectData);
          };
        }

        if (isBudgetNotificationType(data.type ?? '')) {
          return () => {
            notification.destroy(key);
            navigateToBudget(objectData);
          };
        }

        return undefined;
      };

      const { title, description } = getNotificationContent(
        data.type ?? '',
        objectData ?? null,
        data.message,
      );

      notification.open({
        key,
        message: null,
        description: React.createElement(NotificationToast, {
          title,
          description: description ?? data.message,
          onDismiss: handleDismiss,
          onView: getViewHandler(),
        }),
        placement: 'topRight',
        duration: 0,
        className: 'fms-custom-notification',
        closeIcon: null,
        style: {
          borderLeft: `4px solid ${BlueColors[60]}`,
          borderRadius: '8px',
        },
      });
    },
    [navigateToStock, navigateToBudget],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      invalidate({
        resource: 'notification-web',
        invalidates: ['list'],
      });

      try {
        const data = JSON.parse(event.data);
        showToast({
          message: data.message ?? event.data,
          objectData:
            typeof data.objectData === 'string'
              ? JSON.parse(data.objectData)
              : data.objectData,
          type: data.type,
        });
      } catch {
        showToast({ message: event.data });
      }
    },
    [invalidate, showToast],
  );

  const handleConnect = useCallback(() => {
    if (!currentOrganizationId) return;
    reprocessNotifications({
      resource: `notification-web/reprocess`,
      values: {},
      successNotification: false,
      meta: { organizationId: currentOrganizationId },
    });
  }, [apiUrl, currentOrganizationId, reprocessNotifications]);

  useWebSocket({
    url: WS_NOTIFICATION_URL,
    onMessage: handleMessage,
    onConnect: handleConnect,
    enabled: !!currentOrganizationId,
  });

  return unreadCountData?.total ?? 0;
};

export { useNotifications, useNotificationUnreadCount };
